import { useEffect, useRef, useState } from "react";
import {
  BriefcaseBusiness,
  Building2,
  ExternalLink,
  GraduationCap,
  Loader2,
  Mail,
  MapPin,
  Phone,
  UserRound,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import {
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import apiService from "../../services/apiService";
// import { updateDirectorCibilContact } from "../../services/cibilApi";
import { DIRECTOR_APOLLO_TEST_PAYLOAD } from "./directorTestData";
import { normalizeDirectorApolloPayload } from "./normalizeDirectorProfile";
import type { DirectorProfile } from "./types";

const CONTACT_AUTOSAVE_MS = 300;

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zM8.5 8.5h3.8v2h.05c.53-1 1.82-2.05 3.75-2.05 4.01 0 4.75 2.64 4.75 6.07V23h-4v-6.6c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.53 1.71-2.53 3.48V23h-4V8.5z" />
  </svg>
);

const monthLabel = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const period = (start: string | null, end: string | null, current?: boolean) =>
  [monthLabel(start), current ? "Present" : monthLabel(end)]
    .filter(Boolean)
    .join(" – ") || "Dates not listed";

const duration = (start: string | null, end: string | null, current?: boolean) => {
  if (!start) return null;
  const from = new Date(start);
  const to = current || !end ? new Date() : new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const months = Math.max(
    0,
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
  );
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [years ? `${years}y` : null, rest ? `${rest}m` : null].filter(Boolean).join(" ") || "1m";
};

export type McaDirectorInfo = {
  director_uuid?: string;
  name: string;
  designation?: string;
  pan?: string;
  din?: string;
  gender?: string;
  nationality?: string;
  date_of_birth?: string;
  age?: number;
  date_of_appointment_for_current_designation?: string;
  din_status?: string;
  date_of_appointment?: string;
};

export type McaCompanyNetwork = {
  cin: string;
  legal_name: string;
  company_status: string;
  designation: string;
  date_of_appointment: string;
  date_of_cessation: string | null;
  city: string;
};

type DirectorApolloPanelProps = {
  open: boolean;
  director: McaDirectorInfo | null;
  customerId?: string;
  presentDirectorships?: McaCompanyNetwork[];
  pastDirectorships?: McaCompanyNetwork[];
  /** Keep true until director Apollo API is live. */
  useTestData?: boolean;
  /** Called after email/phone are saved (also used to refresh CIBIL row contact). */
  onContactSaved?: (contact: {
    directorUuid: string;
    email: string;
    phone: string;
  }) => void;
};

const formatMcaDate = (dateString?: string | null) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export function DirectorApolloPanel({
  open,
  director,
  customerId,
  presentDirectorships = [],
  pastDirectorships = [],
  useTestData = false,
  onContactSaved,
}: DirectorApolloPanelProps) {
  const [profile, setProfile] = useState<DirectorProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingTestData, setUsingTestData] = useState(false);
  const [editingContact, setEditingContact] = useState<"email" | "phone" | null>(
    null
  );
  const [emailDraft, setEmailDraft] = useState("");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [contactSaveState, setContactSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const contactSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedContact = useRef({ email: "", phone: "" });

  useEffect(() => {
    // profile.email / profile.phone already prefer API data.email & data.mobileNumber
    // (via normalize), then fall back to Apollo person.email / person phone.
    const email = profile?.email ?? "";
    const phone = profile?.phone ?? "";
    setEmailDraft(email);
    setPhoneDraft(phone);
    lastSavedContact.current = { email, phone };
  }, [profile?.email, profile?.phone, profile?.apolloPersonId]);

  useEffect(() => {
    return () => {
      if (contactSaveTimer.current) clearTimeout(contactSaveTimer.current);
    };
  }, []);

  const persistContact = async (email: string, phone: string) => {
    if (!customerId || !director?.director_uuid) return;
    const savedCustomerId = customerId;
    const savedDirectorUuid = director.director_uuid;
    const nextEmail = email.trim();
    const nextPhone = phone.trim();
    if (
      nextEmail === lastSavedContact.current.email &&
      nextPhone === lastSavedContact.current.phone
    ) {
      return;
    }

    setContactSaveState("saving");
    try {
      // Persist on Apollo/director profile (top-level email/phone/mobile).
      await apiService.post("/customers/updateDirectorApolloContact", {
        customerId: savedCustomerId,
        directorUuid: savedDirectorUuid,
        director_uuid: savedDirectorUuid,
        din: director.din,
        name: director.name,
        email: nextEmail || null,
        mobileNumber: nextPhone || null,
        phone: nextPhone || null,
        mobile: nextPhone || null,
      });

      // Also update CIBIL director row JSON (used by fetch-CIBIL form).
     

      lastSavedContact.current = { email: nextEmail, phone: nextPhone };
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              email: nextEmail || null,
              phone: nextPhone || null,
              phoneStatus: nextPhone ? "available" : prev.phoneStatus,
            }
          : prev
      );
      onContactSaved?.({
        directorUuid: savedDirectorUuid,
        email: nextEmail,
        phone: nextPhone,
      });
      setContactSaveState("saved");
    } catch (caught) {
      console.error("Failed to save director contact", caught);
      setContactSaveState("error");
      toast.error("Failed to save contact");
    }
  };

  const scheduleContactSave = (email: string, phone: string) => {
    if (contactSaveTimer.current) clearTimeout(contactSaveTimer.current);
    contactSaveTimer.current = setTimeout(() => {
      void persistContact(email, phone);
    }, CONTACT_AUTOSAVE_MS);
  };

  const loadProfile = async (forceRefresh = false) => {
    if (!director) return;
    const setBusy = forceRefresh ? setIsEnriching : setLoading;
    setBusy(true);
    setError(null);
    try {
      let payload: unknown = null;
      if (customerId && (director.din || director.director_uuid)) {
        try {
          const response = await apiService.post<any>(
            "/customers/getDirectorApolloProfile",
            {
              customerId,
              directorUuid: director.director_uuid,
              director_uuid: director.director_uuid,
              din: director.din,
              name: director.name,
              refresh: forceRefresh || undefined,
            }
          );
          // apiService already returns response.data; some APIs nest again under .data
          payload = response?.data ?? response;
        } catch {
          payload = null;
        }
      }

      let next = payload ? normalizeDirectorApolloPayload(payload) : null;
      if (!next && useTestData) {
        next = normalizeDirectorApolloPayload(DIRECTOR_APOLLO_TEST_PAYLOAD);
        setUsingTestData(true);
      } else {
        setUsingTestData(false);
      }

      if (!next) {
        setProfile(null);
        setError("No professional profile found for this director yet.");
        return;
      }
      setProfile(next);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Director enrichment failed."
      );
      if (useTestData) {
        setProfile(normalizeDirectorApolloPayload(DIRECTOR_APOLLO_TEST_PAYLOAD));
        setUsingTestData(true);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open || !director) {
      setProfile(null);
      setError(null);
      setUsingTestData(false);
      return;
    }
    void loadProfile(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, director?.din, director?.name, customerId]);

  if (!director) return null;

  if (loading) {
    return (
      <div className="space-y-5 p-6">
        <SheetHeader className="border-b bg-muted/40 px-0 pb-5">
          <SheetTitle>Director Details</SheetTitle>
          {/* <SheetDescription>
            Company and professional profile from Apollo
          </SheetDescription> */}
        </SheetHeader>
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-52 w-full" />
      </div>
    );
  }

  // if (!profile) {
  //   return (
  //     <div className="mx-auto flex min-h-[50vh] max-w-md flex-col justify-center px-6 py-12 text-center">
  //       <SheetHeader className="mb-6 text-left">
  //         <SheetTitle>Director Details</SheetTitle>
  //         {/* <SheetDescription>
  //           Company and professional profile from Apollo
  //         </SheetDescription> */}
  //       </SheetHeader>
  //       <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground">
  //         <Sparkles className="h-6 w-6" />
  //       </span>
  //       <h2 className="mt-5 text-xl font-semibold">Enrich {director.name}</h2>
  //       <p className="mt-2 text-sm text-muted-foreground">
  //           Fetch best matching professional profile for this director.
  //         </p>
  //       {error && (
  //         <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-left text-sm text-destructive">
  //           {error}
  //         </p>
  //       )}
  //       <Button
  //         className="mt-4"
  //         onClick={() => void loadProfile(true)}
  //         disabled={isEnriching}
  //       >
  //         {isEnriching ? (
  //           <Loader2 className="h-4 w-4 mr-2 animate-spin" />
  //         ) : (
  //           <Sparkles className="h-4 w-4 mr-2" />
  //         )}
  //         {isEnriching ? "Fetching director profile…" : "Fetch director profile"}
  //       </Button>
  //     </div>
  //   );
  // }

  return (
    <div>
      <SheetHeader className="border-b bg-muted/40 px-6 py-5 pr-14">
        <SheetTitle>Director Details</SheetTitle>
        {/* <SheetDescription>
          Company and professional profile from Apollo
          {usingTestData ? " (test data)" : ""}
        </SheetDescription> */}
      </SheetHeader>

      <section className="border-b px-6 py-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          {profile?.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt={`${profile?.fullName} profile`}
              className="h-24 w-24 rounded-md border object-cover"
            />
          ) : (
            <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
              <UserRound className="h-10 w-10" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">{profile?.fullName ?? director.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {profile?.headline ??
                    profile?.currentTitle ??
                    director.designation ??
                    "Professional profile"}
                </p>
              </div>
              {/* <Button
                size="sm"
                variant="outline"
                onClick={() => void loadProfile(true)}
                disabled={isEnriching}
                title="Refresh director profile"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1 ${isEnriching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button> */}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {profile?.currentCompany && (
                <Badge variant="secondary" className="gap-1">
                  <Building2 className="h-3.5 w-3.5" /> {profile?.currentCompany}
                </Badge>
              )}
              {[profile?.city, profile?.state, profile?.country].filter(Boolean)
                .length > 0 && (
                <Badge variant="outline" className="gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {[profile?.city, profile?.state, profile?.country]
                    .filter(Boolean)
                    .join(", ")}
                </Badge>
              )}
                {/* <Badge variant="outline">
                  {(profile?.matchConfidence ?? "best match").replace(/_/g, " ")}
                </Badge> */}
              {profile?.seniority && (
                <Badge variant="outline">
                  {profile?.seniority.replace(/_/g, " ")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 p-6 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="space-y-6">
          {/* <section>
            <h3 className="text-sm font-semibold">Overview</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {profile.overview ??
                "No overview available for this person."}
            </p>
          </section> */}

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Contact</h3>
              {contactSaveState === "saving" && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </span>
              )}
              {contactSaveState === "saved" && (
                <span className="text-xs text-muted-foreground">Saved</span>
              )}
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                {editingContact === "email" ? (
                  <Input
                    autoFocus
                    type="email"
                    value={emailDraft}
                    placeholder="Enter email"
                    className="h-8"
                    onChange={(e) => {
                      const next = e.target.value;
                      setEmailDraft(next);
                      scheduleContactSave(next, phoneDraft);
                    }}
                    onBlur={() => {
                      setEditingContact(null);
                      if (contactSaveTimer.current) {
                        clearTimeout(contactSaveTimer.current);
                        contactSaveTimer.current = null;
                      }
                      void persistContact(emailDraft, phoneDraft);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                      if (e.key === "Escape") {
                        setEmailDraft(lastSavedContact.current.email);
                        setEditingContact(null);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="w-full rounded-md px-1 py-0.5 text-left hover:bg-muted/60"
                    onClick={() => setEditingContact("email")}
                    title="Click to edit email"
                  >
                    <p>{emailDraft || "Email unavailable"}</p>
                    {profile?.emailStatus && (
                      <p className="text-xs text-muted-foreground">
                        {profile.emailStatus}
                      </p>
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                {editingContact === "phone" ? (
                  <Input
                    autoFocus
                    type="tel"
                    value={phoneDraft}
                    placeholder="Enter mobile number"
                    className="h-8"
                    onChange={(e) => {
                      const next = e.target.value;
                      setPhoneDraft(next);
                      scheduleContactSave(emailDraft, next);
                    }}
                    onBlur={() => {
                      setEditingContact(null);
                      if (contactSaveTimer.current) {
                        clearTimeout(contactSaveTimer.current);
                        contactSaveTimer.current = null;
                      }
                      void persistContact(emailDraft, phoneDraft);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                      if (e.key === "Escape") {
                        setPhoneDraft(lastSavedContact.current.phone);
                        setEditingContact(null);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="w-full rounded-md px-1 py-0.5 text-left hover:bg-muted/60"
                    onClick={() => setEditingContact("phone")}
                    title="Click to edit mobile"
                  >
                    <p>
                      {phoneDraft ||
                        (profile?.phoneStatus === "pending"
                          ? "Mobile number on its way…"
                          : "Mobile number unavailable")}
                    </p>
                    {/* <p className="text-xs text-muted-foreground">
                      {phoneDraft
                        ? profile?.phoneStatus ?? "available"
                        : profile?.phoneStatus === "pending"
                          ? "Phone enrichment is in progress — it usually lands within a minute."
                          : "Click to add a mobile number"}
                    </p> */}
                  </button>
                )}
              </div>
            </div>
            {profile?.linkedinUrl && (
              <a
                href={profile?.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-sm text-primary hover:underline"
              >
                <LinkedInIcon className="h-4 w-4" /> Open LinkedIn{" "}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </section>

          {/* <p className="text-xs text-muted-foreground">
            Last enriched {new Date(profile?.enrichedAt ?? "").toLocaleString("en-IN")}
          </p> */}
        </div>

        <Accordion
          type="multiple"
          defaultValue={[
            "director",
            "work",
            "education",
            "regulatory",
            "present-directorships",
            "past-directorships",
          ]}
          className="border-t"
        >
          <AccordionItem value="director">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <UserRound className="h-4 w-4" /> Director Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Current role</dt>
                  <dd className="mt-1 font-medium">
                    {profile?.currentTitle ?? director.designation ?? "Not listed"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Seniority</dt>
                  <dd className="mt-1 font-medium">
                    {profile?.seniority
                      ? profile?.seniority.replace(/_/g, " ")
                      : "Not listed"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Company</dt>
                  <dd className="mt-1 font-medium">
                    {profile?.currentCompany ?? "Not listed"}
                  </dd>
                </div>
                {/* <div>
                  <dt className="text-muted-foreground">Company domain</dt>
                  <dd className="mt-1 font-medium">
                    {profile?.companyDomain ?? "Not listed"}
                  </dd>
                </div> */}
                {!!profile?.departments?.length && (
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Departments</dt>
                    <dd className="mt-1 flex flex-wrap gap-1.5">
                      {profile.departments.map((dept) => (
                        <Badge key={dept} variant="outline" className="text-xs">
                          {dept.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="regulatory">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Regulatory Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <dl className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <dt className="text-muted-foreground">PAN</dt>
                  <dd className="mt-1 font-medium">{director.pan || "-"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">DIN</dt>
                  <dd className="mt-1 font-medium">{director.din || "-"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Gender</dt>
                  <dd className="mt-1 font-medium">{director.gender || "-"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Nationality</dt>
                  <dd className="mt-1 font-medium">
                    {director.nationality || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Date of Birth</dt>
                  <dd className="mt-1 font-medium">
                    {formatMcaDate(director.date_of_birth)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Age</dt>
                  <dd className="mt-1 font-medium">
                    {director.age && director.age > 0 ? `${director.age} years` : "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Appointment Date</dt>
                  <dd className="mt-1 font-medium">
                    {formatMcaDate(
                      director.date_of_appointment_for_current_designation
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">DIN Status</dt>
                  <dd className="mt-1 font-medium">
                    {director.din_status || "-"}
                  </dd>
                </div>
              </dl>
            </AccordionContent>
          </AccordionItem>

          {!!profile?.employmentHistory?.length && (
            <AccordionItem value="work">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <BriefcaseBusiness className="h-4 w-4" /> Work Experience (
                  {profile.employmentHistory.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="divide-y">
                  {profile.employmentHistory.map((job, index) => {
                    const span = duration(job.startDate, job.endDate, job.current);
                    return (
                      <li
                        key={`${job.company}-${job.title}-${index}`}
                        className="flex items-center gap-4 py-4"
                      >
                        {job.logoUrl ? (
                          <img
                            src={job.logoUrl}
                            alt={`${job.company} logo`}
                            loading="lazy"
                            className="h-10 w-10 shrink-0 rounded-lg border bg-background object-contain p-1"
                          />
                        ) : (
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted">
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{job.company}</p>
                          <p className="truncate text-sm text-muted-foreground">
                            {job.title}
                            {span ? ` • ${span}` : ""}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm text-muted-foreground">
                          {period(job.startDate, job.endDate, job.current)}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}

          {!!profile?.education?.length && (
            <AccordionItem value="education">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" /> Education (
                  {profile.education.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {profile.education.map((item, index) => (
                    <div key={`${item.school}-${index}`}>
                      <p className="font-medium">{item.school}</p>
                      <p className="text-muted-foreground">
                        {[item.degree, item.field].filter(Boolean).join(", ") ||
                          "Qualification not listed"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {period(item.startDate, item.endDate)}
                      </p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="present-directorships">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Present Directorships (
                {presentDirectorships.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {presentDirectorships.length ? (
                <div className="space-y-3">
                  {presentDirectorships.map((company, index) => (
                    <div
                      key={`${company.cin}-present-${index}`}
                      className="rounded-md border p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-sm">{company.legal_name}</h4>
                        <Badge variant="success">{company.designation}</Badge>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {/* <p>CIN: {company.cin}</p> */}
                        <p>Appointed: {formatMcaDate(company.date_of_appointment)}</p>
                        {/* <p>City: {company.city}</p> */}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No present directorships found
                </p>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="past-directorships">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Past Directorships (
                {pastDirectorships.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {pastDirectorships.length ? (
                <div className="space-y-3">
                  {pastDirectorships.map((company, index) => (
                    <div
                      key={`${company.cin}-past-${index}`}
                      className="rounded-md border p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-sm">{company.legal_name}</h4>
                        <Badge variant="destructive">{company.designation}</Badge>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {/* <p>CIN: {company.cin}</p> */}
                        <p>Appointed: {formatMcaDate(company.date_of_appointment)}</p>
                        <p>Ceased: {formatMcaDate(company.date_of_cessation)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No past directorships found
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
